import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Form, FormField, Input, ModalDialog, Select, TranslatableField } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { SERVICE_CLASS_CODES, ServiceClassCode } from "../../../../http/screwConnector/types/common";
import {
  InsertServiceClassBody,
  ServiceClass,
  UpdateServiceClassBody,
} from "../../../../http/screwConnector/types/normativeTables";
import { useScrewConnectorServiceClassesApi } from "../../../../http/screwConnector/useAdminNormativeTables";
import { translatableFieldProps } from "../../../../utils/translatableField";

interface ServiceClassFormBody {
  code: ServiceClassCode | null;
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
}

interface ServiceClassFormProps {
  serviceClass?: ServiceClass;
  onClose: () => void;
}

const ServiceClassForm: FC<ServiceClassFormProps> = ({ serviceClass, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.screwConnectorModule.normativeTables.serviceClasses;
  const dispatch = useContext(NotificationDispatch);
  const api = useScrewConnectorServiceClassesApi();

  const defaultValues: ServiceClassFormBody = {
    code: serviceClass?.code ?? null,
    name: serviceClass?.name ?? null,
    nameIt: serviceClass?.nameIt ?? null,
    nameDe: serviceClass?.nameDe ?? null,
    nameEs: serviceClass?.nameEs ?? null,
    nameFr: serviceClass?.nameFr ?? null,
  };

  const onSubmit = () => async (formData: ServiceClassFormBody) => {
    if (formData.code == null || !formData.name) {
      return;
    }

    const body: UpdateServiceClassBody = {
      name: formData.name,
      nameIt: formData.nameIt || null,
      nameDe: formData.nameDe || null,
      nameEs: formData.nameEs || null,
      nameFr: formData.nameFr || null,
    };

    try {
      if (serviceClass) {
        await api.updateServiceClass(serviceClass.code, body);
      } else {
        const insertBody: InsertServiceClassBody = { code: formData.code, ...body };
        await api.insertServiceClass(insertBody);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        serviceClass ? `${i18n.translation.common.edit} ${t.singular}` : `${i18n.translation.common.add} ${t.singular}`
      }
    >
      <Form<ServiceClassFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control, register, formState: { errors }, reset, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [serviceClass]);

          useEffect(() => {
            register("nameIt");
            register("nameDe");
            register("nameEs");
            register("nameFr");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="code"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={t.fields.code} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select<{ value: ServiceClassCode; label: string }, false>
                        inputId={labelId}
                        isDisabled={!!serviceClass}
                        options={SERVICE_CLASS_CODES.map((value) => ({ value, label: String(value) }))}
                        value={field.value != null ? { value: field.value, label: String(field.value) } : null}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => String(option.value)}
                        isInvalid={isInvalid}
                        onChange={(option) => field.onChange(option?.value ?? null)}
                      />
                    )}
                  </FormField>
                )}
              />
              <TranslatableField
                {...translatableFieldProps("name", values, setValue, i18n.translation)}
                dialogTitle={t.fields.name}
              >
                <FormField label={t.fields.name} error={errors.name} isOptional={false}>
                  {({ labelId, isOptional, isInvalid }) => (
                    <Input id={labelId} isInvalid={isInvalid} {...register("name", { required: !isOptional, minLength: 1 })} />
                  )}
                </FormField>
              </TranslatableField>
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};

export default ServiceClassForm;
